import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";

export type NavMode = "standard" | "command";

interface NavModeContextValue {
  navMode: NavMode;
  setNavMode: (mode: NavMode) => void;
  toggleNavMode: () => void;
}

const NavModeContext = createContext<NavModeContextValue | null>(null);

const STORAGE_KEY = "navMode";

function readInitial(): NavMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "command" || v === "standard") return v;
  } catch {}
  return "standard";
}

export function NavModeProvider({ children }: { children: ReactNode }) {
  const [navMode, setNavModeState] = useState<NavMode>(readInitial);

  const setNavMode = useCallback((mode: NavMode) => {
    setNavModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {}
  }, []);

  const toggleNavMode = useCallback(() => {
    setNavMode(navMode === "standard" ? "command" : "standard");
  }, [navMode, setNavMode]);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === "command" || e.newValue === "standard")) {
        setNavModeState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <NavModeContext.Provider value={{ navMode, setNavMode, toggleNavMode }}>
      {children}
    </NavModeContext.Provider>
  );
}

export function useNavMode() {
  const ctx = useContext(NavModeContext);
  if (!ctx) throw new Error("useNavMode must be used within NavModeProvider");
  return ctx;
}
